import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Shield, ShieldCheck, ShieldAlert, Play, History,
  Loader2, AlertTriangle, CheckCircle, XCircle,
  Wrench, Activity, Clock, Zap, Settings, Search,
  ChevronDown, ChevronUp, BarChart3, Timer, Bug,
  RefreshCw, Eye, Radio, Cpu, Mail,
} from "lucide-react";

const SEV = {
  critical: { color: "#dc2626", bg: "bg-red-50", border: "border-red-200", text: "text-red-600" },
  high: { color: "#ea580c", bg: "bg-orange-50", border: "border-orange-200", text: "text-orange-600" },
  medium: { color: "#d97706", bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-600" },
  low: { color: "#2563eb", bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-600" },
};

const RESULT = {
  auto_resolved: { label: "Corrigido", icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-50" },
  alert_created: { label: "Alerta", icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-50" },
  error: { label: "Erro", icon: XCircle, color: "text-red-600", bg: "bg-red-50" },
  rate_limited: { label: "Bloqueado", icon: Shield, color: "text-gray-500", bg: "bg-gray-50" },
  detected: { label: "Detectado", icon: Eye, color: "text-blue-600", bg: "bg-blue-50" },
};

const SCAN_LABELS = { fast: "Fast", standard: "Standard", deep: "Deep" };
const SCAN_ICONS = { fast: Zap, standard: Radio, deep: Cpu };

// ──── Stats Card ────
function StatsCard({ label, value, icon: Icon, color = "text-gray-800", sub, highlight }) {
  return (
    <div className={`rounded-lg border p-3 ${highlight ? "border-red-300 bg-red-50/50" : "bg-white"}`}>
      <div className="flex items-center gap-2">
        {Icon && <Icon className={`w-3.5 h-3.5 ${color}`} />}
        <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</span>
      </div>
      <p className={`text-lg font-black mt-0.5 ${color}`}>{value ?? "—"}</p>
      {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
    </div>
  );
}

// ──── Scan Status Card ────
function ScanCard({ type, data, nextAt }) {
  const Icon = SCAN_ICONS[type] || Radio;
  const last = data?.timestamp?.slice(11, 19) || "—";
  const next = nextAt?.slice(11, 19) || "—";
  return (
    <div className="rounded-lg border bg-white p-3">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-3.5 h-3.5 text-emerald-600" />
        <span className="text-xs font-bold">{SCAN_LABELS[type] || type}</span>
        <Badge variant="outline" className="text-[8px] h-4 ml-auto">{data?.duration_ms || 0}ms</Badge>
      </div>
      <div className="grid grid-cols-2 gap-x-3 text-[10px]">
        <div><span className="text-muted-foreground">Ultimo:</span> <span className="font-mono font-bold">{last}</span></div>
        <div><span className="text-muted-foreground">Proximo:</span> <span className="font-mono">{next}</span></div>
        <div><span className="text-muted-foreground">Incidentes:</span> <span className="font-bold">{data?.total_incidents || 0}</span></div>
        <div><span className="text-muted-foreground">Corrigidos:</span> <span className="font-bold text-emerald-600">{data?.auto_resolved || 0}</span></div>
      </div>
    </div>
  );
}

// ──── Incident Row ────
function IncidentRow({ inc }) {
  const sev = SEV[inc.severity] || SEV.low;
  const res = RESULT[inc.result] || RESULT.detected;
  const ResIcon = res.icon;
  return (
    <tr className="border-b border-muted/30 hover:bg-muted/20 transition-colors text-[11px]" data-testid={`incident-${inc.incident_id?.slice(0,8)}`}>
      <td className="py-2 px-2 font-mono">{inc.timestamp?.slice(11, 19)}</td>
      <td className="py-2 px-2 max-w-[100px] truncate">{inc.restaurant_name}</td>
      <td className="py-2 px-2">
        <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold ${sev.bg} ${sev.text} ${sev.border} border`}>
          {inc.severity?.toUpperCase()}
        </span>
      </td>
      <td className="py-2 px-2 max-w-[120px] truncate">{inc.rule?.replace(/_/g, " ")}</td>
      <td className="py-2 px-2">
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold ${res.bg} ${res.color}`}>
          <ResIcon className="w-2.5 h-2.5" />{res.label}
        </span>
      </td>
      <td className="py-2 px-2 max-w-[180px] truncate text-muted-foreground">{inc.summary}</td>
      <td className="py-2 px-2 font-mono text-muted-foreground">{inc.duration_ms}ms</td>
      <td className="py-2 px-2">
        <Badge variant="outline" className="text-[7px] h-3.5">{inc.execution_mode === "auto" ? "AGEND" : "MANUAL"}</Badge>
      </td>
    </tr>
  );
}

// ──── Main Tab ────
export function JarvisTab({ adminAxios }) {
  const [dashboard, setDashboard] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterSeverity, setFilterSeverity] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [debugging, setDebugging] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [autoFixing, setAutoFixing] = useState(false);
  const [applyingFix, setApplyingFix] = useState(false);
  const [fixLog, setFixLog] = useState(null);
  const [showLog, setShowLog] = useState(false);
  const [engineDash, setEngineDash] = useState(null);
  const [stats, setStats] = useState(null);
  const [showRules, setShowRules] = useState(false);
  const [showIncidents, setShowIncidents] = useState(true);
  const [showStats, setShowStats] = useState(false);
  const [showEmailCfg, setShowEmailCfg] = useState(false);
  const [emailCfg, setEmailCfg] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, alertsRes, engineRes, statsRes] = await Promise.all([
        adminAxios.get("/jarvis/dashboard"),
        adminAxios.get(`/jarvis/alerts${filterStatus ? `?status=${filterStatus}` : ""}${filterSeverity ? `${filterStatus ? "&" : "?"}severity=${filterSeverity}` : ""}`),
        adminAxios.get("/jarvis/engine/dashboard").catch(() => ({ data: null })),
        adminAxios.get("/jarvis/engine/stats").catch(() => ({ data: null })),
      ]);
      setDashboard(dashRes.data);
      // /jarvis/alerts returns { alerts: [...], total: N } — normalise to array
      const rawAlerts = alertsRes.data;
      setAlerts(Array.isArray(rawAlerts) ? rawAlerts : rawAlerts?.alerts ?? []);
      setEngineDash(engineRes.data);
      setStats(statsRes.data);
    } catch {
      toast.error("Erro ao carregar Jarvis");
    }
    setLoading(false);
  }, [adminAxios, filterStatus, filterSeverity]);

  useEffect(() => { fetchData(); const iv = setInterval(fetchData, 15000); return () => clearInterval(iv); }, [fetchData]);

  const runScan = async () => {
    setScanning(true);
    try {
      await adminAxios.post("/jarvis/scan");
      toast.success("Scan completo");
      fetchData();
    } catch { toast.error("Erro no scan"); }
    setScanning(false);
  };

  const autoFixAll = async () => {
    setAutoFixing(true);
    try {
      const res = await adminAxios.post("/jarvis/auto-fix-all");
      toast.success(`${res.data?.results?.length || 0} fix(es) aplicado(s)`);
      fetchData();
    } catch { toast.error("Erro no auto-fix"); }
    setAutoFixing(false);
  };

  const debugAlert = async (alertId) => {
    setDebugging(true);
    try {
      const res = await adminAxios.post(`/jarvis/alerts/${alertId}/debug`);
      setSelectedAlert(a => a ? { ...a, debug_session: res.data } : a);
      toast.success("Debug concluido");
    } catch { toast.error("Erro no debug"); }
    setDebugging(false);
  };

  const applyFix = async (alertId) => {
    setApplyingFix(true);
    try {
      await adminAxios.post(`/jarvis/alerts/${alertId}/apply-fix`);
      toast.success("Fix aplicado");
      setSelectedAlert(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao aplicar fix");
    }
    setApplyingFix(false);
  };

  const fetchFixLog = async () => {
    if (fixLog) { setShowLog(!showLog); return; }
    try {
      const res = await adminAxios.get("/jarvis/fix-log");
      setFixLog(res.data);
      setShowLog(true);
    } catch { toast.error("Erro ao carregar log"); }
  };

  const resolveAlert = async (alertId) => {
    try {
      await adminAxios.patch(`/jarvis/alerts/${alertId}`, { status: "resolved" });
      toast.success("Alerta resolvido");
      setSelectedAlert(null);
      fetchData();
    } catch { toast.error("Erro"); }
  };

  const saveEmailCfg = async (cfg) => {
    try {
      await adminAxios.put("/jarvis/engine/config", cfg);
      toast.success("Config email salva");
      fetchData();
    } catch { toast.error("Erro ao salvar config"); }
  };

  const deleteAlert = async (alertId) => {
    try {
      await adminAxios.delete(`/jarvis/alerts/${alertId}`);
      toast.success("Alerta removido");
      setSelectedAlert(null);
      fetchData();
    } catch { toast.error("Erro"); }
  };

  if (loading && !dashboard) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  const filteredAlerts = (alerts || []).filter(a =>
    !searchQuery || a.title?.toLowerCase().includes(searchQuery.toLowerCase()) || a.restaurant_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const ed = engineDash || {};
  const todayStats = ed.today || {};

  return (
    <div className="space-y-4" data-testid="jarvis-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-emerald-500/10"><ShieldCheck className="w-6 h-6 text-emerald-600" /></div>
          <div>
            <h2 className="font-black text-lg tracking-tight">JARVIS 2.0</h2>
            <p className="text-[10px] text-muted-foreground">Auto-Remediacao Inteligente | Fast 1m | Standard 5m | Deep 30m</p>
          </div>
          <Badge variant={ed.enabled ? "default" : "destructive"} className="text-[10px] h-5">{ed.enabled ? "MOTOR ATIVO" : "DESLIGADO"}</Badge>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" className="gap-1 h-7 text-[10px] bg-emerald-600 hover:bg-emerald-700" onClick={runScan} disabled={scanning} data-testid="scan-all-btn">
            {scanning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}Scan Completo
          </Button>
          <Button size="sm" variant="outline" className="gap-1 h-7 text-[10px]" onClick={autoFixAll} disabled={autoFixing} data-testid="auto-fix-btn">
            {autoFixing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wrench className="w-3 h-3" />}Auto-Fix
          </Button>
          <Button size="sm" variant="ghost" className="gap-1 h-7 text-[10px]" onClick={fetchFixLog} data-testid="fix-log-btn">
            <History className="w-3 h-3" />Log
          </Button>
        </div>
      </div>

      {/* Scan Type Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {["fast", "standard", "deep"].map(st => (
          <ScanCard key={st} type={st} data={ed.last_scans?.[st]} nextAt={ed.next_scans?.[st]} />
        ))}
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <StatsCard label="Incidentes Hoje" value={todayStats.total || 0} icon={Bug} color="text-gray-800" />
        <StatsCard label="Correcoes Auto" value={todayStats.auto_resolved || 0} icon={CheckCircle} color="text-emerald-600" />
        <StatsCard label="Alertas" value={todayStats.alerts || 0} icon={AlertTriangle} color="text-amber-600" />
        <StatsCard label="Falhas" value={todayStats.errors || 0} icon={XCircle} color="text-red-600" highlight={todayStats.errors > 0} />
        <StatsCard label="MTTD" value={stats ? `${stats.mttd_ms}ms` : "—"} icon={Timer} color="text-blue-600" sub="Tempo medio deteccao" />
        <StatsCard label="MTTR" value={stats ? `${stats.mttr_ms}ms` : "—"} icon={RefreshCw} color="text-purple-600" sub="Tempo medio resolucao" />
      </div>

      {/* MTTD/MTTR Stats Panel */}
      {stats && (
        <Card>
          <CardContent className="p-4">
            <button className="flex items-center gap-2 w-full text-left" onClick={() => setShowStats(!showStats)} data-testid="toggle-stats">
              <BarChart3 className="w-4 h-4 text-purple-500" />
              <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground flex-1">Metricas do Motor</span>
              <div className="flex gap-3 text-[10px] text-muted-foreground">
                <span>Auto: <strong className="text-emerald-600">{stats.auto_resolve_pct}%</strong></span>
                <span>Detectados: <strong>{stats.total_detected}</strong></span>
                <span>Resolvidos: <strong>{stats.total_resolved}</strong></span>
              </div>
              {showStats ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            {showStats && (
              <div className="mt-3 space-y-3">
                {/* Per-rule */}
                {stats.per_rule && Object.keys(stats.per_rule).length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold text-muted-foreground mb-1.5">Por Regra</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                      {Object.entries(stats.per_rule).filter(([,v]) => v.detected > 0).map(([rule, v]) => (
                        <div key={rule} className="text-[10px] border rounded p-1.5 bg-muted/20">
                          <span className="font-medium">{rule.replace(/_/g, " ")}</span>
                          <div className="flex gap-2 mt-0.5 text-muted-foreground">
                            <span>{v.detected} det</span>
                            <span className="text-emerald-600">{v.success} ok</span>
                            {v.fail > 0 && <span className="text-red-600">{v.fail} fail</span>}
                            <span>{v.success_rate_pct}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Per-restaurant */}
                {(stats.per_restaurant || []).length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold text-muted-foreground mb-1.5">Por Restaurante (24h)</p>
                    <div className="space-y-1">
                      {stats.per_restaurant.map((r, i) => (
                        <div key={i} className="flex items-center gap-2 text-[10px]">
                          <span className="w-28 truncate font-medium">{r.name}</span>
                          <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                            <div className="bg-emerald-500 h-1.5 rounded-full" style={{
                              width: `${Math.min(100, (r.resolved / Math.max(1, r.detected)) * 100)}%`
                            }} />
                          </div>
                          <span className="text-muted-foreground w-16 text-right">{r.resolved}/{r.detected}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Email Config */}
      <Card data-testid="email-config">
        <CardContent className="p-4">
          <button className="flex items-center gap-2 w-full text-left" onClick={() => { setShowEmailCfg(!showEmailCfg); if (!showEmailCfg && ed.email_config) setEmailCfg({ email_cooldown_minutes: ed.email_config.cooldown_minutes, email_min_severity: ed.email_config.min_severity, email_suppress_known: ed.email_config.suppress_known }); }} data-testid="toggle-email-cfg">
            <Mail className="w-4 h-4 text-blue-500" />
            <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground flex-1">Config Email</span>
            <div className="flex gap-3 text-[10px] text-muted-foreground">
              <span>Cooldown: <strong>{ed.email_config?.cooldown_minutes || 30}min</strong></span>
              <span>Sev min: <strong>{ed.email_config?.min_severity || "critical"}</strong></span>
              {ed.email_config?.last_email_at && <span>Ultimo: <strong className="font-mono">{ed.email_config.last_email_at?.slice(11, 19)}</strong></span>}
            </div>
            {showEmailCfg ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {showEmailCfg && (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
              <div>
                <label className="text-[10px] font-bold text-muted-foreground block mb-1">Cooldown (minutos)</label>
                <input type="number" min="5" max="1440" className="h-7 w-full text-xs px-2 rounded border" value={emailCfg.email_cooldown_minutes ?? ed.email_config?.cooldown_minutes ?? 30} onChange={e => setEmailCfg(c => ({ ...c, email_cooldown_minutes: parseInt(e.target.value) || 30 }))} data-testid="email-cooldown-input" />
              </div>
              <div>
                <label className="text-[10px] font-bold text-muted-foreground block mb-1">Severidade Minima</label>
                <select className="h-7 w-full text-xs px-2 rounded border" value={emailCfg.email_min_severity ?? ed.email_config?.min_severity ?? "critical"} onChange={e => setEmailCfg(c => ({ ...c, email_min_severity: e.target.value }))} data-testid="email-severity-select">
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={emailCfg.email_suppress_known ?? ed.email_config?.suppress_known ?? true} onCheckedChange={v => setEmailCfg(c => ({ ...c, email_suppress_known: v }))} data-testid="email-suppress-toggle" />
                <label className="text-[10px] font-bold text-muted-foreground">Suprimir estados conhecidos</label>
              </div>
              <Button size="sm" className="h-7 text-[10px] bg-blue-600 hover:bg-blue-700" onClick={() => saveEmailCfg(emailCfg)} data-testid="save-email-cfg">Salvar</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top Rules + Circuit Breakers + Severity */}
      <div className="grid md:grid-cols-3 gap-3">
        {/* Top Rules */}
        {(ed.top_rules_today || []).length > 0 && (
          <Card>
            <CardContent className="p-3">
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5"><Activity className="w-3 h-3" /> Regras Mais Acionadas</p>
              <div className="space-y-1.5">
                {ed.top_rules_today.map((r, i) => (
                  <div key={i} className="flex items-center gap-2 text-[10px]">
                    <span className="w-5 text-right font-black text-emerald-600">{r.count}</span>
                    <div className="flex-1 bg-emerald-100 rounded-full h-1.5">
                      <div className="bg-emerald-500 h-1.5 rounded-full" style={{
                        width: `${Math.min(100, (r.count / (ed.top_rules_today[0]?.count || 1)) * 100)}%`
                      }} />
                    </div>
                    <span className="text-muted-foreground truncate max-w-[80px]">{r.rule?.replace(/_/g, " ")}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Severity Today */}
        {ed.severity_today && Object.keys(ed.severity_today).length > 0 && (
          <Card>
            <CardContent className="p-3">
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5"><ShieldAlert className="w-3 h-3" /> Severidade Hoje</p>
              <div className="space-y-1.5">
                {Object.entries(ed.severity_today).sort(([,a],[,b]) => b - a).map(([sev, count]) => {
                  const s = SEV[sev] || SEV.low;
                  return (
                    <div key={sev} className="flex items-center gap-2 text-[10px]">
                      <span className={`w-5 text-right font-black ${s.text}`}>{count}</span>
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[8px] font-bold ${s.bg} ${s.text} border ${s.border}`}>{sev.toUpperCase()}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Circuit Breakers */}
        {ed.circuit_breakers && (
          <Card>
            <CardContent className="p-3">
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5"><AlertTriangle className="w-3 h-3" /> Circuit Breakers</p>
              <div className="space-y-1">
                {Object.entries(ed.circuit_breakers).filter(([, cb]) => cb.failures > 0 || cb.tripped).map(([rule, cb]) => (
                  <div key={rule} className="flex items-center gap-2 text-[10px]">
                    <div className={`w-2 h-2 rounded-full ${cb.tripped ? "bg-red-500 animate-pulse" : "bg-amber-500"}`} />
                    <span className="flex-1 truncate">{rule.replace(/_/g, " ")}</span>
                    <span className="text-muted-foreground font-mono">{cb.failures}/3</span>
                    {cb.tripped && <Badge variant="destructive" className="text-[7px] h-3.5">TRIP</Badge>}
                  </div>
                ))}
                {Object.values(ed.circuit_breakers).every(cb => cb.failures === 0 && !cb.tripped) && (
                  <p className="text-[10px] text-muted-foreground flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-500" /> Todos saudaveis</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Rules Config */}
      <Card data-testid="rules-config">
        <CardContent className="p-4">
          <button className="flex items-center gap-2 w-full text-left" onClick={() => setShowRules(!showRules)} data-testid="toggle-rules">
            <Settings className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground flex-1">
              Regras ({Object.values(ed.rules || {}).filter(r => r.enabled).length}/{Object.keys(ed.rules || {}).length})
            </span>
            {showRules ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {showRules && (
            <div className="mt-3 space-y-1">
              {["fast", "standard", "deep"].map(scan => {
                const scanRules = Object.entries(ed.rules || {}).filter(([, r]) => r.scan === scan);
                if (!scanRules.length) return null;
                return (
                  <div key={scan}>
                    <p className="text-[10px] font-bold text-muted-foreground mt-2 mb-1 uppercase">{SCAN_LABELS[scan]} Scan</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1.5">
                      {scanRules.map(([key, rule]) => {
                        const modeColor = rule.action_mode === "auto_fix" ? "bg-emerald-50 border-emerald-200" :
                                          rule.action_mode === "alert_only" ? "bg-amber-50 border-amber-200" : "bg-blue-50 border-blue-200";
                        return (
                          <div key={key} className={`text-[10px] p-2 rounded border ${modeColor} ${!rule.enabled ? "opacity-40" : ""}`}>
                            <div className="flex items-center gap-1.5">
                              <div className={`w-1.5 h-1.5 rounded-full ${rule.enabled ? "bg-emerald-500" : "bg-gray-300"}`} />
                              <span className="font-medium truncate flex-1">{key.replace(/_/g, " ")}</span>
                              <Badge variant="outline" className="text-[7px] h-3 px-1">
                                {rule.action_mode === "auto_fix" ? "AUTO" : rule.action_mode === "alert_only" ? "ALERTA" : "CONFIRM"}
                              </Badge>
                            </div>
                            <div className="flex gap-2 mt-0.5 text-muted-foreground">
                              <span>{rule.severity}</span>
                              <span>conf: {rule.confidence}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Incidents Table */}
      <Card data-testid="incidents-table">
        <CardContent className="p-4">
          <button className="flex items-center gap-2 w-full text-left mb-2" onClick={() => setShowIncidents(!showIncidents)} data-testid="toggle-incidents">
            <Bug className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground flex-1">
              Incidentes Recentes ({(ed.recent_incidents || []).length})
            </span>
            {showIncidents ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {showIncidents && (ed.recent_incidents || []).length > 0 && (
            <ScrollArea className="h-[320px]">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-muted/50">
                    {["Hora", "Tenant", "Sev", "Regra", "Resultado", "Resumo", "Tempo", "Modo"].map(h => (
                      <th key={h} className="text-left py-1.5 px-2 text-[9px] font-bold text-muted-foreground uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(ed.recent_incidents || []).map((inc, i) => <IncidentRow key={inc.incident_id || i} inc={inc} />)}
                </tbody>
              </table>
            </ScrollArea>
          )}
          {showIncidents && (!ed.recent_incidents || ed.recent_incidents.length === 0) && (
            <p className="text-center text-[10px] text-muted-foreground py-6">Nenhum incidente registrado</p>
          )}
        </CardContent>
      </Card>

      {/* Classic Alerts (from config scanner) */}
      <Card data-testid="config-alerts">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <ShieldAlert className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground flex-1">Alertas de Configuracao ({filteredAlerts.length})</span>
            <div className="flex gap-1">
              <input
                className="h-6 w-32 text-[10px] px-2 rounded border bg-muted/20"
                placeholder="Buscar..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                data-testid="search-alerts"
              />
              <select className="h-6 text-[10px] px-1 rounded border bg-muted/20" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} data-testid="filter-status">
                <option value="">Todos</option>
                <option value="open">Abertos</option>
                <option value="in_progress">Em Progresso</option>
                <option value="resolved">Resolvidos</option>
              </select>
              <select className="h-6 text-[10px] px-1 rounded border bg-muted/20" value={filterSeverity} onChange={e => setFilterSeverity(e.target.value)} data-testid="filter-severity">
                <option value="">Todas</option>
                <option value="critical">Critica</option>
                <option value="warning">Warning</option>
                <option value="info">Info</option>
              </select>
            </div>
          </div>
          <ScrollArea className="h-[250px]">
            <div className="space-y-1.5">
              {filteredAlerts.map(alert => (
                <div
                  key={alert.id}
                  className="p-2.5 rounded-lg border cursor-pointer hover:bg-muted/30 transition-colors"
                  onClick={() => setSelectedAlert(alert)}
                  data-testid={`alert-${alert.id?.slice(0,8)}`}
                >
                  <div className="flex items-center gap-2 text-xs">
                    <Badge variant={alert.severity === "critical" ? "destructive" : alert.severity === "warning" ? "default" : "secondary"} className="text-[8px] h-4">{alert.severity}</Badge>
                    <span className="font-medium flex-1 truncate">{alert.title}</span>
                    <Badge variant="outline" className="text-[8px] h-4">{alert.status}</Badge>
                    <span className="text-muted-foreground text-[10px]">{alert.restaurant_name}</span>
                  </div>
                </div>
              ))}
              {filteredAlerts.length === 0 && <p className="text-center text-muted-foreground text-[10px] py-4">Nenhum alerta</p>}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Alert Detail Modal */}
      {selectedAlert && (
        <Card className="border-2 border-primary/20 shadow-lg" data-testid="alert-detail">
          <CardContent className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <Badge variant={selectedAlert.severity === "critical" ? "destructive" : "default"} className="text-[10px] mb-1">{selectedAlert.severity} | {selectedAlert.category}</Badge>
                <h3 className="text-sm font-bold">{selectedAlert.title}</h3>
                <p className="text-[10px] text-muted-foreground">{selectedAlert.restaurant_name} | {selectedAlert.created_at?.slice(0, 19)}</p>
              </div>
              <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => setSelectedAlert(null)}>Fechar</Button>
            </div>
            <p className="text-xs text-muted-foreground mb-3">{selectedAlert.description}</p>
            {selectedAlert.suggested_fix && (
              <div className="text-[10px] p-2 bg-emerald-50 rounded border border-emerald-200 mb-3">
                <strong>Fix sugerido:</strong> {selectedAlert.suggested_fix.replace(/_/g, " ")}
              </div>
            )}
            {selectedAlert.debug_session && (
              <div className="text-[10px] p-2 bg-muted rounded border mb-3">
                <strong>Debug:</strong>
                <pre className="mt-1 overflow-auto max-h-24 text-[9px]">{JSON.stringify(selectedAlert.debug_session, null, 2)}</pre>
              </div>
            )}
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" className="h-7 text-[10px] gap-1" onClick={() => debugAlert(selectedAlert.id)} disabled={debugging}>
                {debugging ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}Debug
              </Button>
              {selectedAlert.suggested_fix && !selectedAlert.fix_applied && (
                <Button size="sm" className="h-7 text-[10px] gap-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => applyFix(selectedAlert.id)} disabled={applyingFix}>
                  {applyingFix ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wrench className="w-3 h-3" />}Aplicar Fix
                </Button>
              )}
              <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => resolveAlert(selectedAlert.id)}>Resolver</Button>
              <Button size="sm" variant="destructive" className="h-7 text-[10px]" onClick={() => deleteAlert(selectedAlert.id)}>Remover</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Fix Log */}
      {showLog && fixLog && (
        <Card data-testid="fix-log">
          <CardContent className="p-4">
            <p className="text-xs font-bold text-muted-foreground mb-2 uppercase">Historico de Fixes</p>
            <ScrollArea className="h-[200px]">
              <div className="space-y-1">
                {(fixLog || []).map((log, i) => (
                  <div key={i} className="text-[10px] p-1.5 border rounded flex items-center gap-2">
                    {log.success ? <CheckCircle className="w-3 h-3 text-emerald-500" /> : <XCircle className="w-3 h-3 text-red-500" />}
                    <span className="flex-1 truncate">{log.recipe_id?.replace(/_/g, " ")} — {log.restaurant_name}</span>
                    <span className="text-muted-foreground font-mono">{log.applied_at?.slice(11, 19)}</span>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
